'use client';

import { createContext, useCallback, useContext, useEffect } from 'react';
import { authenticatedUser } from '@/app/utils/authentication';
import { useAuthSystem, useTreatments } from '@/app/treatments-provider';
import type { SubscriptionTier, SubscriptionLevel } from '@/app/utils/checkout-tracking';
import { CHECKOUT_IFRAME_EXPERIMENT } from '@/app/utils/checkout-tracking';
import { useTracking } from './tracking-provider';
import { usePricingSnapshot } from './pricing-provider';
import { isPricingVersion } from '@/app/utils/pricing/validation';
import { reportPricingFailure } from '@/app/utils/pricing/reporting';
import {
  documentFunnelOpening,
  funnelAttribution,
  currentPricingExposure,
  safelyTrack,
  emitFunnelStart,
  type FunnelEvent,
  type FunnelOpening,
  type FunnelSurface,
} from '@/app/utils/funnel-analytics';

export interface FunnelContextValue {
  opening?: FunnelOpening;
  pricing_catalog_version?: import('@/app/utils/pricing/types').PricingVersion | null;
  flow: 'signup' | 'checkout';
  surface: FunnelSurface;
  ui_origin?: string | null;
  plan_intent?: SubscriptionTier | null;
  plan_duration?: SubscriptionLevel | null;
  tier?: SubscriptionTier | null;
}
export const FunnelContext = createContext<FunnelContextValue | null>(null);

export function useFunnelStart(event: FunnelEvent, available = true) {
  const context = useContext(FunnelContext);
  const tracker = useTracking();
  const treatments = useTreatments();
  const authSystem = useAuthSystem();
  const pricing = usePricingSnapshot();
  useEffect(() => {
    if (!available) return;
    safelyTrack(() => {
      const { opening: suppliedOpening, ...dimensions } = context ?? {
        opening: documentFunnelOpening(),
        flow: 'signup',
        surface: 'standalone',
        plan_intent: 'free',
      };
      const opening = suppliedOpening ?? documentFunnelOpening();
      // A once-only start must not freeze unknown exposure during the initial pricing read.
      // A same-origin iframe opening already carries its parent's frozen exposure (including unknown).
      if (
        dimensions.flow === 'checkout' &&
        pricing?.loading &&
        !(
          dimensions.surface === 'iframe' &&
          Object.prototype.hasOwnProperty.call(opening, 'pricing_catalog_version')
        )
      )
        return;
      const experiments = Object.values(treatments).map(({ experiment, bucket }) => ({
        name: experiment.name,
        version: experiment.version,
        variant: bucket.name,
      }));
      if (dimensions.surface === 'iframe' && opening.experiments)
        experiments.push(...opening.experiments);
      if (
        dimensions.surface === 'iframe' &&
        !experiments.some(({ name }) => name === CHECKOUT_IFRAME_EXPERIMENT.name)
      ) {
        const { name, version, bucket } = CHECKOUT_IFRAME_EXPERIMENT;
        experiments.push({ name, version, variant: bucket });
      }
      if (opening.events[event]) return;
      if (authSystem === 'legacy') {
        const user = authenticatedUser();
        if (user) tracker.setUser(user.id);
        else tracker.resetUserIfIdentified();
      }
      emitFunnelStart(tracker, opening, event, {
        ...dimensions,
        pricing_catalog_version:
          dimensions.surface === 'iframe' &&
          Object.prototype.hasOwnProperty.call(opening, 'pricing_catalog_version')
            ? isPricingVersion(opening.pricing_catalog_version)
              ? opening.pricing_catalog_version
              : null
            : dimensions.pricing_catalog_version !== undefined
              ? dimensions.pricing_catalog_version
              : (pricing?.config?.pricing_catalog_version ??
                (dimensions.flow === 'signup' ? currentPricingExposure() : null)),
        ...(event === 'Signup Begin' ? { state_name: 'index.next' } : {}),
        ...(experiments.length ? { experiments } : {}),
      });
    });
  }, [available, context, event, tracker, treatments, authSystem, pricing]);
}

/** Read on submission/completion so the effect's immutable snapshot is available. */
export function useFunnelAttribution() {
  const context = useContext(FunnelContext);
  return useCallback(() => {
    try {
      return funnelAttribution(context?.opening ?? documentFunnelOpening());
    } catch (error) {
      reportPricingFailure('funnel_attribution', error);
      return {}; // Optional attribution never blocks signup or payment.
    }
  }, [context?.opening]);
}
